/* eslint-disable react/jsx-max-depth */
import { memo } from 'react';

import { ExternalLink } from '@doger/ui/components/ui/link';

import { LogAmplitudeEventOnMount } from '@doger/chat/shared/analytics';

import styles from './cookie-policy.module.scss';

const lastUpdated = '06.10.2025';

export const CookiePolicyPage = memo(() => (
  <>
    <LogAmplitudeEventOnMount event='Cookie Policy Page Viewed' />

    <div className={styles.cookiePolicyPage}>
      <div className={styles.container}>
        <h1 className={styles.title}>COOKIE POLICY</h1>

        <div className={styles.content}>
          <div className={styles.versionInfo}>
            <p className={styles.paragraph}>
              V.1.0. Last updated: {lastUpdated}
            </p>
          </div>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>INTRODUCTION</h2>
            <p className={styles.paragraph}>
              This Cookie Policy ("Policy") describes how Doger ("we", "our",
              "Application") uses cookies and similar technologies in our mobile
              application and website.
            </p>
            <p className={styles.paragraph}>
              This Policy is an integral part of the Terms of Use and Privacy
              Policy of Doger.
            </p>
            <p className={styles.paragraph}>
              <strong>Contact information:</strong> doger@layer2state.com
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>1. WHAT ARE COOKIES?</h2>
            <p className={styles.paragraph}>
              A <strong>cookie</strong> is a small text file that is stored on
              your device (computer, smartphone, tablet) when you use the
              Application or visit our website.
            </p>

            <h3 className={styles.subsectionTitle}>Types of cookies:</h3>

            <h4 className={styles.subsubsectionTitle}>By duration:</h4>
            <ul className={styles.simpleList}>
              <li>
                <strong>Session cookies</strong> — automatically deleted when
                you close the application or browser
              </li>
              <li>
                <strong>Persistent cookies</strong> — stored on your device
                until a specified expiry date or until manually deleted
              </li>
            </ul>

            <h4 className={styles.subsubsectionTitle}>By source:</h4>
            <ul className={styles.simpleList}>
              <li>
                <strong>First-party cookies</strong> — set directly by Doger
              </li>
              <li>
                <strong>Third-party cookies</strong> — set by third parties
                (e.g., for analytics)
              </li>
            </ul>

            <p className={styles.paragraph}>
              Most cookies collect anonymized data about how users interact with
              the Application and website.
            </p>
            <p className={styles.paragraph}>
              <strong>Additional information:</strong>{' '}
              <ExternalLink href='https://www.aboutcookies.org'>
                www.aboutcookies.org
              </ExternalLink>
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>
              2. PURPOSES OF USING COOKIES
            </h2>
            <p className={styles.paragraph}>Doger uses cookies for:</p>
            <ul className={styles.simpleList}>
              <li>
                <strong>Ensuring Application and website functionality</strong>{' '}
                — authentication, security, saving preferences
              </li>
              <li>
                <strong>Language detection</strong> — automatically determining
                your browser language for better user experience
              </li>
              <li>
                <strong>Analytics</strong> — understanding how users interact
                with the Application and website, identifying issues
              </li>
              <li>
                <strong>Service improvement</strong> — optimizing features based
                on usage data
              </li>
              <li>
                <strong>Marketing</strong> — measuring advertising campaign
                effectiveness
              </li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>3. COOKIES WE USE</h2>

            <h3 className={styles.subsectionTitle}>
              Table 1. Strictly Necessary Cookies
            </h3>
            <p className={styles.paragraph}>
              <em>
                These cookies are essential for the Application and website to
                function and cannot be disabled (Services can not be supplied
                without them).
              </em>
            </p>

            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Purpose</th>
                    <th>Expiry</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>G_AUTHUSER_H</td>
                    <td>Cookies required to sign in with Google Account</td>
                    <td>1 year</td>
                  </tr>
                  <tr>
                    <td>G_ENABLED_IDPS</td>
                    <td>Cookies required to sign in with Google Account</td>
                    <td>400 days</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className={styles.subsectionTitle}>
              Table #2. Analytical Cookies
            </h3>
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Purpose</th>
                    <th>Expiry</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>_ga</td>
                    <td>
                      Google Analytics cookies required for the service to work
                    </td>
                    <td>2 years</td>
                  </tr>
                  <tr>
                    <td>_ga_*</td>
                    <td>
                      Google Analytics cookies required for the service to work
                    </td>
                    <td>1 year</td>
                  </tr>
                  <tr>
                    <td>amp_*</td>
                    <td>Amplitude cookies required for the service to work</td>
                    <td>1 year</td>
                  </tr>
                  <tr>
                    <td>amplitude_id_*</td>
                    <td>Amplitude cookies required for the service to work</td>
                    <td>1 year</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className={styles.subsectionTitle}>
              Table #3. List of Marketing Cookies
            </h3>
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Purpose</th>
                    <th>Expiry</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>MgidSensorHref</td>
                    <td>Cookies used for Mgid service operation</td>
                    <td>20 days</td>
                  </tr>
                  <tr>
                    <td>MgidSensorNVis</td>
                    <td>Cookies used for Mgid service operation</td>
                    <td>20 days</td>
                  </tr>
                  <tr>
                    <td>_fbp</td>
                    <td>
                      Facebook cookies used to distinguish users who attended
                      the website with the help of Facebook ads
                    </td>
                    <td>3 months</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className={styles.subsectionTitle}>
              Table #4. List of Unclassified Cookies
            </h3>
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Purpose</th>
                    <th>Expiry</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>theme</td>
                    <td>
                      Cookies used to store the website theme settings (dark or
                      light)
                    </td>
                    <td>1 year</td>
                  </tr>
                  <tr>
                    <td>language</td>
                    <td>Store browser language preference</td>
                    <td>1 year</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>4. MANAGING COOKIES</h2>

            <h3 className={styles.subsectionTitle}>
              4.1 In-App and Website Settings
            </h3>
            <p className={styles.paragraph}>
              You can manage optional cookies through the Application or website
              settings:
            </p>
            <ol className={styles.simpleList}>
              <li>
                Open the settings menu in the Application or on the website
              </li>
              <li>Go to "Privacy and Security" section</li>
              <li>Select "Manage Cookies"</li>
              <li>
                Enable or disable cookie categories (analytical, marketing)
              </li>
            </ol>
            <p className={styles.paragraph}>
              <strong>Please note:</strong> strictly necessary cookies cannot be
              disabled as they ensure basic functionality of the Services.
            </p>

            <h3 className={styles.subsectionTitle}>4.2 Browser Management</h3>
            <p className={styles.paragraph}>
              You can manage cookies through your browser settings:
            </p>

            <h4 className={styles.subsubsectionTitle}>Deleting cookies:</h4>
            <p className={styles.paragraph}>
              You can delete all saved cookies through your browser history.
              This will remove saved preferences and login data.
            </p>

            <h4 className={styles.subsubsectionTitle}>Blocking cookies:</h4>
            <p className={styles.paragraph}>
              Most modern browsers allow you to block cookies. However, this may
              limit the Application's and website's functionality.
            </p>

            <p className={styles.paragraph}>
              <strong>Instructions for popular browsers:</strong>
            </p>
            <ul className={styles.simpleList}>
              <li>
                <ExternalLink href='https://support.google.com/chrome/answer/95647'>
                  Google Chrome
                </ExternalLink>
              </li>
              <li>
                <ExternalLink href='https://support.apple.com/guide/safari/manage-cookies-sfri11471'>
                  Safari
                </ExternalLink>
              </li>
              <li>
                <ExternalLink href='https://support.mozilla.org/kb/cookies-information-websites-store-on-your-computer'>
                  Firefox
                </ExternalLink>
              </li>
            </ul>

            <h3 className={styles.subsectionTitle}>
              4.3 Opt-out of Marketing Cookies
            </h3>
            <p className={styles.paragraph}>
              You can opt out of personalized advertising through advertising
              system settings:
            </p>
            <ul className={styles.simpleList}>
              <li>
                Google:{' '}
                <ExternalLink href='https://adssettings.google.com'>
                  https://adssettings.google.com
                </ExternalLink>
              </li>
              <li>
                Facebook:{' '}
                <ExternalLink href='https://www.facebook.com/ads/preferences'>
                  https://www.facebook.com/ads/preferences
                </ExternalLink>
              </li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>5. THIRD-PARTY SERVICES</h2>
            <p className={styles.paragraph}>
              Some cookies are set by our partners to provide functionality and
              analytics:
            </p>

            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Service</th>
                    <th>Purpose</th>
                    <th>Privacy Policy</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Google Analytics</td>
                    <td>Application and website usage analysis</td>
                    <td>
                      <ExternalLink href='https://policies.google.com/privacy'>
                        https://policies.google.com/privacy
                      </ExternalLink>
                    </td>
                  </tr>
                  <tr>
                    <td>Amplitude</td>
                    <td>Product analytics</td>
                    <td>
                      <ExternalLink href='https://amplitude.com/privacy'>
                        https://amplitude.com/privacy
                      </ExternalLink>
                    </td>
                  </tr>
                  <tr>
                    <td>Meta (Facebook)</td>
                    <td>Advertising effectiveness measurement</td>
                    <td>
                      <ExternalLink href='https://www.facebook.com/privacy'>
                        https://www.facebook.com/privacy
                      </ExternalLink>
                    </td>
                  </tr>
                  <tr>
                    <td>Mgid</td>
                    <td>Advertising campaign measurement</td>
                    <td>
                      <ExternalLink href='https://www.mgid.com/privacy-policy'>
                        https://www.mgid.com/privacy-policy
                      </ExternalLink>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>
              6. LEGAL BASIS FOR PROCESSING
            </h2>
            <p className={styles.paragraph}>
              In accordance with applicable data protection legislation (GDPR,
              CCPA, and others), we process cookies on the following legal
              bases:
            </p>
            <ul className={styles.simpleList}>
              <li>
                <strong>Strictly necessary cookies:</strong> contract
                performance and security provision — these cookies are necessary
                to provide you with the Application's and website's services
              </li>
              <li>
                <strong>Analytical cookies:</strong> your consent and our
                legitimate interests in improving the Application and website
              </li>
              <li>
                <strong>Marketing cookies:</strong> your explicit consent
              </li>
            </ul>
            <p className={styles.paragraph}>
              You may withdraw your consent to the use of analytical and
              marketing cookies at any time through the Application or website
              settings.
            </p>
            <p className={styles.paragraph}>
              <strong>Governing Law and Disputes:</strong> Any disputes arising
              from this Cookie Policy shall be governed by and construed in
              accordance with English law. Disputes shall be resolved through
              arbitration under the Arbitration Rules of the Dubai Multi
              Commodities Centre (DMCC) Arbitration Centre, with the seat of
              arbitration in Dubai, United Arab Emirates, as specified in our
              Terms of Use.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>7. YOUR RIGHTS</h2>
            <p className={styles.paragraph}>
              Under data protection legislation, you have certain rights -
              please refer to the respective section of the Privacy Policy to
              know more information.
            </p>
            <p className={styles.paragraph}>
              To exercise your rights, contact us at: doger@layer2state.com
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>
              8. INTERNATIONAL DATA TRANSFERS
            </h2>
            <p className={styles.paragraph}>
              Data collected through cookies is stored and processed on servers
              in various jurisdictions (EU-countries, sometimes others). Some of
              our partners (e.g., Google Analytics, Amplitude) may transfer data
              to other countries.
            </p>
            <p className={styles.paragraph}>
              We ensure an adequate level of protection for your data during
              international transfers in accordance with data protection
              legislation requirements.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>
              9. DATA STORAGE AND SECURITY
            </h2>
            <p className={styles.paragraph}>
              Data collected through cookies is stored for the periods specified
              in the tables above. We apply technical measures to protect data
              from unauthorized access.
            </p>
            <p className={styles.paragraph}>
              Upon expiry, cookies are automatically deleted from your device.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>10. YOUR CONSENT</h2>
            <p className={styles.paragraph}>
              By using the Application or website, you consent to the use of
              strictly necessary cookies.
            </p>
            <p className={styles.paragraph}>
              For optional cookies (analytical and marketing), we request your
              consent upon first use of the Application or website. You can
              change your decision at any time through the settings.
            </p>
            <p className={styles.paragraph}>
              <strong>Age Restriction:</strong> Our services are not intended
              for persons under 14 years of age. We do not knowingly collect
              data from children under 14. If you are under 14, please do not
              use our Application or website.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>11. CHANGES TO THIS POLICY</h2>
            <p className={styles.paragraph}>
              We may update this Policy from time to time. Changes will be
              indicated by the last updated date at the beginning of this
              document.
            </p>
            <p className={styles.paragraph}>
              You may be informed about the changes in the same way as it is
              prescribed in the Terms of Use.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>12. CONTACT INFORMATION</h2>
            <p className={styles.paragraph}>
              If you have questions about this Policy, contact us:
            </p>
            <p className={styles.paragraph}>
              <strong>Email:</strong>{' '}
              <ExternalLink href='mailto:doger@layer2state.com'>
                doger@layer2state.com
              </ExternalLink>
              <br />
              <strong>Subject:</strong> Cookie Policy Inquiry
            </p>
            <p className={styles.paragraph}>
              We will respond to your request within 30 days.
            </p>
            <p className={styles.paragraph}>
              <em>
                This Cookie Policy applies to all users of the Doger Application
                and website.
              </em>
            </p>
          </section>
        </div>
      </div>
    </div>
  </>
));
